import { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import ListItemText from '@mui/material/ListItemText'
import ListItemIcon from '@mui/material/ListItemIcon'
import Logout from '@mui/icons-material/Logout'
import { useNavigate } from 'react-router-dom'

import SelectRole from './SelectRole'
import ToolBarLink from './ToolBarLink'
import FavoriteTutors from './FavoriteTutors/FavoriteTutors'
import Notifications from './Notifications'
import { Avatar } from '../../UI'

import {
  selectRole,
  selectUserData,
  selectRegisterStep,
} from 'routing/fetchingfunction/user/user.reducer'
import { logout } from '../../../routing/fetchingfunction/user/user.action'

import { getAvatarUrl, getProfileByRole } from '../../../helpers'

import { STUDENT_ROLE, TUTOR_ROLE } from 'constants/global'
import { BACKEND_REGISTRATION_CODES } from 'constants/register'

import dashboardIcon from 'assets/icon/dashboard-icon.svg'
import settingsIcon from 'assets/icon/settings-icon.svg'

import LanguageSelect from '../NotLoggedInToolBar/LanguageSelect'

const LoggedInToolBar = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const role = useSelector(selectRole)
  const userData = useSelector(selectUserData)
  const registerStep = useSelector(selectRegisterStep)

  const [anchorEl, setAnchorEl] = useState(null)
  const [registrationComplete, setRegistrationComplete] = useState(false)

  // Redirect to login page if userData is null
  // console.log(userData?.registration_step, registerStep, 'User Data in LoggedInToolBar.jsx')
  useEffect(() => {
    if (!userData) {
      navigate('/login')
      return
    }

    // Check if registration is complete
    const currentStep =
      registerStep || userData?.registration_step || userData?.profile?.current_registration_step
    const isComplete =
      currentStep === BACKEND_REGISTRATION_CODES.COMPLETED || currentStep === 'profile_completed'

    setRegistrationComplete(isComplete)

    // If registration is not complete, redirect to the appropriate registration step
    if (!isComplete && role) {
      // Get path mapping
      const stepToPathMap = {
        create_account: 'create-account',
        email_verification: 'confirm-email',
        profile_update: 'profile-update',
        student_goals: 'goals',
        tutor_test: 'pass-test',
        tutor_kyc: 'kyc',
        tutor_create_banner: 'create-banner',
      }

      // Check if this step is relevant for the current rol
      const isStepRelevantForRole = (step, userRole) => {
        // console.log(step, userRole)
        // console.log(typeof step)

        if (typeof step !== 'string') {
          return
        }
        if (step?.startsWith('student_') && userRole !== STUDENT_ROLE) {
          console.log(
            `[STEP TRACKING] TOOLBAR: Step "${step}" is student-specific but current role is ${userRole}, skipping redirect`
          )
          return false
        }
        if (step?.startsWith('tutor_') && userRole !== TUTOR_ROLE) {
          console.log(
            `[STEP TRACKING] TOOLBAR: Step "${step}" is tutor-specific but current role is ${userRole}, skipping redirect`
          )
          return false
        }

        return true
      }
      // Only redirect if current step is relevant for the user's role and we're not already on a registration page
      const isOnRegisterPage = window.location.pathname.includes('/register/')
      if (!currentStep || typeof currentStep !== 'string') {
        return
      }
      if (!isOnRegisterPage && isStepRelevantForRole(currentStep, role)) {
        const pathSegment = stepToPathMap[currentStep] || 'profile-update'

        navigate(`/${role}/register/${pathSegment}`)
      }
    }
  }, [userData, registerStep, role, navigate])

  // Return null if userData is not available to prevent rendering errors
  if (!userData) {
    return null
  }

  const profile = getProfileByRole(userData, role)
  const isDropdownOpen = Boolean(anchorEl)

  const handleClickAvatar = (event) => {
    setAnchorEl(event.currentTarget)
  }

  const handleCloseDropdown = () => {
    setAnchorEl(null)
  }

  const handleLogout = async () => {
    handleCloseDropdown()

    try {
      // Dispatch logout and wait for it to complete
      await dispatch(logout()).unwrap()
      //console.log('Logout completed, redirecting to homepage')

      if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.log('Logout completed, redirecting to homepage')
      }

      // Only navigate after logout is complete
      navigate('/')
    } catch (error) {
      console.error('Error during logout:', error)
      // Still try to navigate to homepage even if logout fails
      navigate('/')
    }
  }

  return (
    <div className="flex items-center justify-end text-sm gap-4">
      {/* Only show the toolbar links if registration is complete */}
      {registrationComplete && (
        <>
          <SelectRole />

          <ul className="flex items-center justify-center gap-x-[10px] !mr-4 w-[190px]">
            <ToolBarLink href={`/${role}/statistic`} icon={dashboardIcon} label="Dashboard" />

            <ToolBarLink href={`/${role}/settings`} icon={settingsIcon} label="Settings" />

            {role === STUDENT_ROLE && <FavoriteTutors />}

            <Notifications />
          </ul>
        </>
      )}

      <button
        type="button"
        id="user-avatar-dropdown"
        aria-controls={isDropdownOpen ? 'user-avatar-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={isDropdownOpen ? 'true' : undefined}
        onClick={handleClickAvatar}
      >
        <Avatar
          src={getAvatarUrl(profile?.profile_picture)}
          gender={userData?.profile?.gender}
          className="w-[36px] h-[36px]"
        />
      </button>
      <div className="mr-3">
        <LanguageSelect />
      </div>

      <Menu
        id="user-avatar-menu"
        anchorEl={anchorEl}
        open={isDropdownOpen}
        onClose={handleCloseDropdown}
        MenuListProps={{
          'aria-labelledby': 'user-avatar-dropdown',
        }}
      >
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <Logout fontSize="small" className="!text-primary-100" />
          </ListItemIcon>

          <ListItemText className="!text-primary-100">Logout</ListItemText>
        </MenuItem>
      </Menu>
    </div>
  )
}

export default LoggedInToolBar
